import { ChevronDown, ChevronRight, Play, TriangleAlert } from "lucide-react";
import { useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Field, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { useCaseIde } from "../../context/CaseIdeContext";
import { useCaseData } from "../../data/CaseDataContext";
import { promptMeta } from "../catalog";
import { useWorkflows } from "../context/WorkflowsContext";
import { packetContentsOf, selectedParts } from "../packetContents";
import { researchAction } from "../researchTopics";
import { STATUS_META } from "../status";
import type { WorkflowFacts } from "../status";
import type { WorkflowDef, WorkflowScope, WorkflowView } from "../types";
import { FirmDefaultsLink } from "./FirmDefaultsLink";
import { FormsOptions } from "./FormsOptions";
import { LettersOptions } from "./LettersOptions";
import { PacketContentsSelect } from "./PacketContentsSelect";
import { ResearchOptions } from "./ResearchOptions";

type ScopeOption = { id: string; label: string; scope: WorkflowScope };

/**
 * Retitle's choices: every file, its title replaced (the default) and, when
 * only some files still have upload names, just those.
 */
export const retitleOptions = (
  facts: Pick<WorkflowFacts, "files" | "untitled">
): ScopeOption[] => {
  const left = facts.untitled.length;
  return [
    {
      id: "default",
      label: `All Files (${facts.files})`,
      scope: { kind: "default" }
    },
    ...(left > 0 && left < facts.files
      ? [
          {
            id: "uploadNames",
            label: `Only Files With Upload Names (${left})`,
            scope: { kind: "uploadNames" } as const
          }
        ]
      : [])
  ];
};

/** The "Applies To" choices a workflow offers on this case, if any. */
const scopeOptions = (
  def: WorkflowDef,
  facts: WorkflowFacts,
  selected: string[]
): ScopeOption[] => {
  const { files } = facts;
  const selection: ScopeOption[] = selected.length
    ? [
        {
          id: "selection",
          label: `Selected Files (${selected.length})`,
          scope: { kind: "selection", nodeIds: selected }
        }
      ]
    : [];
  if (def.id === "organize")
    return selection.length
      ? [
          {
            id: "default",
            label: `Every File (${files})`,
            scope: { kind: "default" }
          },
          ...selection
        ]
      : [];
  if (def.id === "retitle") {
    const own = retitleOptions(facts);
    return own.length > 1 || selection.length ? [...own, ...selection] : own;
  }
  return [];
};

/** Case Research's checked topics once they load; undefined runs the general prompt. */
const researchTopicsFor = (
  def: WorkflowDef,
  w: ReturnType<typeof useWorkflows>
) =>
  def.id === "research" && w.researchTopics.kind === "ready"
    ? w.topics
    : undefined;

type ConfigureProps = {
  def: WorkflowDef;
  onRun: (scope: WorkflowScope) => void;
  onClose: () => void;
};

/** The chevron's popover: scope, a note for this case, and the exact prompt. */
const Configure: FC<ConfigureProps> = ({ def, onRun, onClose }) => {
  const w = useWorkflows();
  const { selection } = useCaseIde();
  const { fs } = useCaseData();
  const letters = def.id === "letters" ? w.letters : undefined;
  const topics = researchTopicsFor(def, w);
  const packetEmpty =
    def.id === "packet" &&
    selectedParts(
      w.facts.packetParts,
      w.packetPicks,
      packetContentsOf(w.settings.packetSettings)
    ).length === 0;
  const selectedFiles = selection.selectedNodeIds.filter(
    (id) => fs.nodes.find((n) => n.id === id)?.kind === "file"
  );
  const formsEmpty = def.id === "forms" && w.formPicks?.length === 0;
  const options = scopeOptions(def, w.facts, selectedFiles);
  const [scopeId, setScopeId] = useState(options[0]?.id ?? "default");
  const scope =
    options.find((o) => o.id === scopeId)?.scope ??
    ({ kind: "default" } as const);
  const [showPrompt, setShowPrompt] = useState(false);
  const note = w.notes[def.id] ?? "";

  return (
    <div
      className="flex min-h-0 flex-col"
      data-testid={`workflow-configure-${def.id}`}
    >
      <div
        className="flex min-h-0 flex-col gap-3 overflow-y-auto overscroll-contain p-3"
        data-testid={`workflow-configure-body-${def.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-base font-medium text-fg">{def.title}</span>
            <span className="text-sm text-fg-3">{def.summary(w.settings)}</span>
          </div>
          <FirmDefaultsLink className="-mr-1 shrink-0" />
        </div>

        {options.length > 1 ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-fg-2">Applies To</span>
            <div
              role="radiogroup"
              aria-label="Applies To"
              className="flex flex-wrap gap-1"
            >
              {options.map((o) => (
                <Button
                  key={o.id}
                  variant="pill"
                  size="xs"
                  role="radio"
                  aria-checked={o.id === scopeId}
                  className="aria-checked:bg-fill-2 aria-checked:text-fg"
                  onClick={() => setScopeId(o.id)}
                >
                  {o.label}
                </Button>
              ))}
            </div>
          </div>
        ) : null}

        {def.id === "retitle" && scope.kind === "default" ? (
          <p className="text-sm text-fg-3" data-testid="workflow-replace-note">
            Retitles all {w.facts.files} files and replaces their titles,
            including ones renamed by hand. Titles a person marked Keep stay.
            Undo reverts the whole change.
          </p>
        ) : null}

        {def.id === "letters" ? <LettersOptions /> : null}
        {def.id === "forms" ? <FormsOptions /> : null}
        {def.id === "research" ? <ResearchOptions /> : null}
        {def.id === "packet" ? <PacketContentsSelect labelled /> : null}

        <Field className="gap-1.5">
          <FieldLabel
            htmlFor={`workflow-note-${def.id}`}
            className="text-sm font-medium text-fg-2"
          >
            For This Case Only
          </FieldLabel>
          <Textarea
            id={`workflow-note-${def.id}`}
            value={note}
            onChange={(e) => w.setNote(def.id, e.target.value)}
            placeholder="Anything Casey should do differently on this case"
            rows={2}
            className="min-h-14 text-base"
            data-testid={`workflow-note-${def.id}`}
          />
          <span className="text-sm text-fg-4">
            Kept for this case. Firm defaults don't change.
          </span>
        </Field>

        {w.settings.askEveryWrite ? (
          <p className="flex items-start gap-1.5 text-sm text-warn">
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            Ask Before Every Change is on, so this run pauses at each change.
          </p>
        ) : null}

        <Collapsible open={showPrompt} onOpenChange={setShowPrompt}>
          <CollapsibleTrigger className="flex items-center gap-1 text-sm text-fg-2 hover:text-fg">
            {showPrompt ? (
              <ChevronDown className="size-3.5" aria-hidden />
            ) : (
              <ChevronRight className="size-3.5" aria-hidden />
            )}
            What Casey Will Do
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-1.5 flex flex-col gap-1">
              <p
                className="max-h-48 overflow-y-auto rounded-md bg-fill-1 p-2 text-sm whitespace-pre-wrap text-fg-2"
                data-testid={`workflow-prompt-${def.id}`}
              >
                {w.promptFor(def.id, scope, letters, topics)}
              </p>
              <span className="font-mono text-sm text-fg-4">
                {promptMeta(def)}
              </span>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-1.5 border-t border-line-1 px-3 py-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={
            w.facts.files === 0 ||
            letters?.length === 0 ||
            topics?.length === 0 ||
            packetEmpty ||
            formsEmpty ||
            (w.researchTopics.kind === "unavailable" && def.id === "research")
          }
          onClick={() => {
            onRun(scope);
            onClose();
          }}
        >
          <Play />
          Run With Casey
        </Button>
      </div>
    </div>
  );
};

type Props = {
  def: WorkflowDef;
  view: WorkflowView;
  primary?: boolean;
};

/**
 * Run with the firm defaults, or open Configure from the chevron. A running
 * step has none: its turn is in the chat beside it. One that needs an
 * answer opens its thread. A queued one shows "Queued", disabled, so it
 * can't be added twice.
 */
export const WorkflowRunButton: FC<Props> = ({
  def,
  view,
  primary = false
}) => {
  const w = useWorkflows();
  const { individualId, open: openArtifact } = useCaseIde();
  const [open, setOpen] = useState(false);
  const meta = STATUS_META[view.status];

  if (
    def.kind === "direct" ||
    view.status === "running" ||
    ((view.live || view.status === "needs-you") && !view.opens)
  )
    return null;

  const variant = primary ? "primary" : "secondary";
  const fallback =
    view.action ?? meta.action ?? (view.status === "queued" ? "Queued" : "Run");
  const research = researchTopicsFor(def, w);
  const label =
    view.opens === "thread"
      ? view.openLabel ?? "Answer in Chat"
      : view.opens
      ? "Open Case Insights"
      : research && w.researchTopics.kind === "ready" && meta.action
      ? researchAction(research, w.researchTopics.posture, fallback) ?? fallback
      : fallback;
  return (
    <div
      className="flex shrink-0 items-center"
      role="group"
      aria-label={def.title}
    >
      <Button
        variant={variant}
        size="sm"
        className="rounded-r-none"
        disabled={!meta.action && !view.opens}
        title={label}
        onClick={() => {
          if (view.opens === "thread" && view.threadId) {
            w.answer(def.id, view.threadId);
            return;
          }
          if (view.opens) {
            openArtifact({
              kind: "key_facts",
              id: individualId,
              title: "Case Insights"
            });
            return;
          }
          const topics = researchTopicsFor(def, w);
          const packetEmpty =
            def.id === "packet" &&
            selectedParts(
              w.facts.packetParts,
              w.packetPicks,
              packetContentsOf(w.settings.packetSettings)
            ).length === 0;
          const formsEmpty = def.id === "forms" && w.formPicks?.length === 0;
          if (topics?.length === 0 || packetEmpty || formsEmpty) setOpen(true);
          else if (topics)
            w.run(def.id, { kind: "default" }, undefined, topics);
          else if (def.id === "letters" && w.letters.length)
            w.run(def.id, { kind: "default" }, w.letters);
          else w.run(def.id);
        }}
        data-testid={`workflow-run-${def.id}`}
      >
        {view.opens ? null : <Play />}
        <span className="max-w-44 truncate">{label}</span>
      </Button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={variant}
            size="icon-sm"
            className={cn(
              "w-6 rounded-l-none",
              primary ? "border-l border-primary-foreground/25" : "-ml-px"
            )}
            aria-label={`Configure ${def.title}`}
            disabled={view.status === "queued" || view.live}
            data-testid={`workflow-configure-trigger-${def.id}`}
          >
            <ChevronDown />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          collisionPadding={8}
          className="max-h-(--radix-popover-content-available-height) w-96 overflow-hidden p-0"
        >
          <Configure
            def={def}
            onRun={(scope) => {
              const topics = researchTopicsFor(def, w);
              if (topics) w.run(def.id, scope, undefined, topics);
              else
                w.run(
                  def.id,
                  scope,
                  def.id === "letters" && w.letters.length
                    ? w.letters
                    : undefined
                );
            }}
            onClose={() => setOpen(false)}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};
